<script lang="ts" setup>
const props = defineProps<{ data: EventCollection }>()
const classEvent = COLLECTION_CLASS_MAP.events

const description = computed(() => {
  if (!props.data?.description) return ''

  // SSR-safe HTML stripping
  return props.data.description.replace(/<[^>]*>/g, '')
})

useSeoMeta({
  title: props.data?.name,
  description,
  ogDescription: description,
  twitterDescription: description,
  twitterTitle: props.data?.name ?? `We're on ${name}`,
  ogTitle: props.data?.name ?? `We're on ${name}`,
})
defineOgImageComponent('Page', { title: props.data?.name ?? '', image: getImagePath({ collectionId: EVENT_DEFAULT.collectionId, id: props.data.id, src: props.data.image }) })

const schema = computed(() => buildEventSchema({ ...props.data, description: description.value }))

// Precompute the JSON string (SSR safe)
const schemaJson = computed(() => JSON.stringify(schema.value))

useHead({
  link: [
    {
      rel: 'canonical',
      href: getPublicRoute('events', props.data, true),
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: schemaJson.value,
    },
  ],
})
</script>

<template>
  <div class="event">
    <div class="absolute top-0 left-0 right-0">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 320"
      >
        <path
          class="fill-pink"
          fill-opacity="1"
          d="M0,256L48,250.7C96,245,192,235,288,208C384,181,480,139,576,128C672,117,768,139,864,149.3C960,160,1056,160,1152,160C1248,160,1344,160,1392,160L1440,160L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
        />
      </svg>
    </div>
    <div
      class="grid lg:grid-cols-3 gap-12 pt-32 px-6 max-w-7xl mx-auto relative lg:pt-64 items-center"
      :class="classEvent"
    >
      <div class="w-full">
        <ImageFull
          :id="data.id"
          loading="lazy"
          :collection-id="data.collectionId"
          :src="data.image"
          class="w-full h-auto max-w-3xl rounded-lg"
        />
      </div>
      <div class="w-full lg:col-span-2 ">
        <HeadingText
          tag="h1"
          :class="classEvent.heading"
        >
          {{ data.name }}
        </HeadingText>
        <DatesDisplay
          :date-start="data.dateStart"
          :date-end="data.dateEnd"
          class="text-blue font-bold"
        />
      </div>
    </div>
    <main class="max-w-7xl p-6 mx-auto bg-slate-50 rounded-lg my-12 gap-6 flex flex-col">
      <section
        v-if="data?.description"
        class="htmldescription py-12 max-w-4xl mx-auto lg:text-lg"
      >
        <div
          data-allow-mismatch
          v-html="data.description"
        />
      </section>
      <div v-if="data.map">
        <p
          class="font-display text-xl lg:text-2xl uppercase font-black"
          :class="classEvent.heading"
        >
          Location
        </p>
        <LocationMap
          v-if="data.map"
          :items="[{ map: data.map }]"
          class="mt-6 rounded-lg overflow-clip"
        />
      </div>
      <div class="flex gap-4 flex-wrap items-center mt-6">
        <p
          class="font-display text-xl lg:text-2xl uppercase font-black"
          :class="classEvent.heading"
        >
          Share This Event!
        </p>
        <SocialShare
          facebook
          twitter
          linked-in
        />
      </div>
      <CollectionReviewAll
        v-if="data.id"
        id="reviews"
        :record-id="data.id"
        :record-name="data.name"
        collection="event"
        :average-rating="0"
        class="bg-slate-100 p-3"
      />
      <CollectionBusinessBrief
        v-if="data?.expand?.business"
        :data="data.expand.business"
        class="mt-6"
      />
    </main>
  </div>
</template>

<style scoped>
.event {
  position: relative
}
</style>
